import {
  SearchField,
  Skeleton,
  Table,
  Typography,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import { AttributeRow } from "./attribute-row";

import {
  useDeleteVariantAttribute,
  useGetVariantAttributes,
  variantAttributeKeys,
} from "@/hooks/api/variant-attributes";
import { queryClient } from "@/main";
import { VariantAttribute } from "@/types/product";

const COLUMNS = [
  { uid: "name", name: "Name", className: "min-w-56" },
  { uid: "options", name: "Options", className: "min-w-72" },
  { uid: "createdAt", name: "Created At", className: "min-w-40" },
  { uid: "actions", name: "Actions" },
];

interface VariantAttributesListProps {
  onEditAttribute: (attribute: VariantAttribute) => void;
}

export const VariantAttributesList = ({
  onEditAttribute,
}: VariantAttributesListProps) => {
  const { data: attributes, isLoading } = useGetVariantAttributes();
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText] = useDebounceValue(searchText, 300);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { mutate: deleteAttribute, isPending: isDeleting } =
    useDeleteVariantAttribute();

  const invalidateAttributes = () => {
    queryClient.invalidateQueries({
      queryKey: variantAttributeKeys.lists(),
    });
  };

  const handleDelete = (attribute: VariantAttribute) => {
    deleteAttribute(attribute.id, {
      onSuccess: () => {
        invalidateAttributes();
        setDeletingId(null);
      },
    });
  };

  const filteredAttributes = useMemo(() => {
    if (!attributes) return [];
    if (!debouncedSearchText.trim()) return attributes;

    const searchLower = debouncedSearchText.toLowerCase();

    return attributes.filter((attr) =>
      attr.name.toLowerCase().includes(searchLower),
    );
  }, [attributes, debouncedSearchText]);

  const skeletonItems = useMemo(
    () => Array.from({ length: 5 }, (_, i) => ({ id: `skeleton-${i}` })),
    [],
  );

  return (
    <>
      <div className="flex flex-col gap-4 h-full">
        <div className="flex items-center gap-4">
          <SearchField value={searchText} onChange={setSearchText}>
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input
                className="min-w-[300px]"
                placeholder="Search attributes..."
              />
              <SearchField.ClearButton />
            </SearchField.Group>
          </SearchField>
        </div>

        <Table>
          <Table.ScrollContainer>
            <Table.Content aria-label="Variant attributes table">
              <Table.Header className="sticky top-0 z-10 bg-surface-secondary">
                {COLUMNS.map((column) => (
                  <Table.Column
                    key={column.uid}
                    className={
                      column.uid === "actions"
                        ? "text-small font-medium flex items-center justify-end px-[20px]"
                        : `text-small font-medium ${column.className ?? ""}`
                    }
                    id={column.uid}
                  >
                    {column.name}
                  </Table.Column>
                ))}
              </Table.Header>
              <Table.Body
                className="text-small"
                renderEmptyState={() => (
                  <div className="flex flex-col items-center justify-center gap-2 py-12">
                    <Icon
                      className="text-muted"
                      fontSize={40}
                      icon="solar:tuning-2-linear"
                    />
                    <div className="flex flex-col items-center gap-0.5">
                      <Typography className="text-sm font-medium text-muted">
                        {debouncedSearchText.trim()
                          ? "No attributes found matching your search"
                          : "No attributes available"}
                      </Typography>
                      <Typography className="text-xs text-muted">
                        Default attributes are created automatically for
                        ecommerce organizations
                      </Typography>
                    </div>
                  </div>
                )}
              >
                {(isLoading ? skeletonItems : filteredAttributes).map((row) => (
                  <Table.Row key={row.id} className="text-small" id={row.id}>
                    {isLoading ? (
                      COLUMNS.map((col) => (
                        <Table.Cell
                          key={col.uid}
                          className="before:bg-transparent py-1.5 px-3"
                        >
                          <Skeleton className="h-8 w-full rounded-lg" />
                        </Table.Cell>
                      ))
                    ) : (
                      <AttributeRow
                        attribute={row as VariantAttribute}
                        deletingId={deletingId}
                        isDeleting={isDeleting}
                        setDeletingId={setDeletingId}
                        onDelete={handleDelete}
                        onEdit={() =>
                          onEditAttribute(row as VariantAttribute)
                        }
                      />
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      </div>
    </>
  );
};
